import { DepthLightCustom } from '@repo/go-icons';
import { TableCell, TableDatePicker, TableInput, TableNumberInput, TableSelect } from '@repo/go-table';
import type { GridColDef } from '@repo/go-table';
import { format, millisecondsToSeconds } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tag } from '../../shared/components/Tag/Tag';
import type { TimeZonesStore } from '../../shared/stores/TimeZonesStore';
import { convertDepthUnitValue } from '../../shared/utils';
import tableStyles from '../AlertsManagerTable.module.css';
import { TableHeader } from '../components/TableHeader/TableHeader';
import { UserInfo } from '../components/UserInfo';
import type { TRiskLevel, TOption } from '../types';

import { ManualAnomalyCell } from './components/ManualAnomalyCell';
import { ManualAnomalyDateCell } from './components/ManualAnomalyDateCell';
import { FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY } from './FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY';
import type { ManualAnomaliesTableStore } from './ManualAnomaliesTableStore';
import type { ManualAnomaliesService } from './services/ManualAnomaliesService';
import type { ManualAnomaly } from './services/ManualAnomaly.entity';
import { type TIndexFormat, type TManualAnomalyRowData } from './types';
import { MANUAL_ANOMALIES_GROUPED_COLUMN_FIELDS } from './useColumnGroups';

const riskToStatus = {
  HIGH_RISK: 'red',
  MED_RISK: 'orange',
  LOW_RISK: 'blue',
  NO_RISK: 'gray',
} as const;

export const useManualAnomaliesColumns = (
  timeZonesStore: TimeZonesStore,
  service: ManualAnomaliesService,
  store: ManualAnomaliesTableStore,
  defaultDepthUnit: 'm' | 'ft',
) => {
  const { t } = useTranslation();
  const editing = service.uiState.editing;
  const getDate = timeZonesStore.getDate;
  const scaleDepthUnit = service.settingsStore.scaleDepthUnit;

  const riskLevelOptions = useMemo(
    () => [
      { label: t('alertsManagerWidget:riskLevelOptions.NO_RISK'), value: 'NO_RISK', status: 'gray' },
      { label: t('alertsManagerWidget:riskLevelOptions.LOW_RISK'), value: 'LOW_RISK', status: 'blue' },
      { label: t('alertsManagerWidget:riskLevelOptions.MED_RISK'), value: 'MED_RISK', status: 'orange' },
      { label: t('alertsManagerWidget:riskLevelOptions.HIGH_RISK'), value: 'HIGH_RISK', status: 'red' },
    ],
    [t],
  );

  const indexFormatOptions = useMemo(
    () => [
      { value: 'LOG_TIME', label: t('alertsManagerWidget:autoAnomalies.indexFormat.time') },
      { value: 'LOG_DEPTH', label: t('alertsManagerWidget:autoAnomalies.indexFormat.depth') },
    ],
    [t],
  );

  const wellboreOptions = useMemo(
    () => service.wellbores.list.map((wellbore) => ({ value: wellbore.id, label: wellbore.name })),
    [service.wellbores.list],
  );

  const anomalyTypeOptions: TOption[] = useMemo(
    () =>
      service.anomalyTypes.map((anomalyType) => ({
        label: anomalyType.data['name']?.toString() ?? '',
        value: anomalyType.id as number,
      })),
    [service.anomalyTypes],
  );

  const getAnomalySubtypeOptions = useCallback(
    function getAnomalySubtypeOptions(anomalyTypeId: number): TOption[] {
      return service.anomalySubtypes
        .filter((anomalySubtype) => anomalySubtype.data['anomalyType'] === anomalyTypeId)
        .map((anomalySubtype) => ({
          label: anomalySubtype.data['name']?.toString() ?? '',
          value: anomalySubtype.id as number,
        }));
    },
    [service.anomalySubtypes],
  );

  return useMemo((): GridColDef<ManualAnomaly>[] => {
    const textCell = (
      field: keyof TManualAnomalyRowData,
      options?: {
        unit?: string;
        icon?: React.ReactNode;
        minWidth?: number;
        width?: number;
        readModeClassName?: string;
      },
    ): GridColDef<ManualAnomaly> => ({
      field: field as string,
      sortable: true,
      filterable: true,
      resizable: true,
      disableReorder: !editing,
      width: options?.width,
      minWidth: options?.minWidth ?? 110,
      flex: 0.6,
      headerAlign: 'left',
      align: 'left',
      cellClassName: () => {
        return editing ? 'go-table__disabled' : (options?.readModeClassName ?? '');
      },
      valueGetter(_, row, col) {
        if (col.field != null) {
          return store.getFieldValue(row.entityId as string, col.field as keyof TManualAnomalyRowData);
        }

        return null;
      },
      renderHeader: (params) => (
        <TableHeader
          field={params.field}
          textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY[params.field] ?? params.field}
          icon={options?.icon}
          unit={options?.unit}
          tableSubHeader={MANUAL_ANOMALIES_GROUPED_COLUMN_FIELDS.has(params.field)}
        />
      ),
      renderCell(params) {
        const v = params.value;
        return <TableCell style={{}}>{v == null || v === '' ? '-' : String(v)}</TableCell>;
      },
    });

    const baseColumn: Omit<GridColDef, 'field'> = {
      resizable: true,
      disableColumnMenu: editing,
      disableReorder: !editing,
      sortable: !editing,
      editable: editing,
      minWidth: 61,
      headerAlign: 'left',
      align: 'left',
      valueGetter(_, row, col) {
        if (col.field != null) {
          return store.getFieldValue(row.entityId as string, col.field as keyof TManualAnomalyRowData);
        }

        return null;
      },
      renderHeader: (params) => (
        <TableHeader
          field={params.field}
          textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY[params.field]}
          tableSubHeader={MANUAL_ANOMALIES_GROUPED_COLUMN_FIELDS.has(params.field)}
        />
      ),

      renderCell(params) {
        return <ManualAnomalyCell validationService={service.validationService} params={params} />;
      },
    };

    const getInverDate = (value: number) => {
      return millisecondsToSeconds(timeZonesStore.getInverDate(value * 1000).getTime());
    };

    const getCreatedAtZoned = (row: ManualAnomaly) => {
      const anomaly = service.manualAnomaliesMap.get(row.entityId as string);
      if (!anomaly) return null;
      const date = new Date(anomaly.data.updatedAt ?? anomaly.createdAt).valueOf();
      return getDate(date);
    };

    const unitOptions = service.unitsList.map((u) => ({
      value: u.id as number,
      label: u.data['abbreviation']?.toString()?.trim() ?? u.data['name']?.toString()?.trim() ?? String(u.id),
    }));

    const isLogTimeFormat = (rowId: string) => store.getFieldValue(rowId, 'indexFormat') === 'LOG_TIME';

    return [
      {
        ...baseColumn,
        field: 'indexFormat',
        minWidth: 100,
        type: 'singleSelect',
        valueOptions: indexFormatOptions,
        valueGetter: (_, row) => store.getFieldValue(row.entityId as string, 'indexFormat'),
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={indexFormatOptions}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(
                  params.row.entityId as string,
                  'indexFormat',
                  value as TIndexFormat,
                )
              }
            />
          );
        },
      },
      {
        field: 'eventDate',
        sortable: true,
        filterable: true,
        editable: editing,
        resizable: true,
        disableReorder: !editing,
        minWidth: 100,
        valueGetter: (_, row) => store.getFieldValue(row.entityId as string, 'anomalyAt'),
        cellClassName: (params) => {
          return editing && !isLogTimeFormat(params.row.entityId as string) ? 'go-table__disabled' : '';
        },
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY.eventDate} />
        ),
        renderCell(params) {
          return (
            <ManualAnomalyDateCell
              validationService={service.validationService}
              params={params}
              timeZonesStore={timeZonesStore}
              format={(date) => {
                return date ? format(date, 'dd.MM.yyyy') : '-';
              }}
            />
          );
        },
        renderEditCell(params) {
          const isTimeFormat = isLogTimeFormat(params.row.entityId as string);
          return (
            <TableDatePicker
              cellParams={params}
              value={params.value}
              disabled={!isTimeFormat}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(
                  params.row.entityId as string,
                  'anomalyAt',
                  value ? getInverDate(value) : undefined,
                )
              }
            />
          );
        },
      },
      {
        field: 'eventTime',
        sortable: true,
        filterable: true,
        editable: editing,
        resizable: true,
        disableReorder: !editing,
        minWidth: 100,
        valueGetter: (_, row) => store.getFieldValue(row.entityId as string, 'anomalyAt'),
        cellClassName: (params) => {
          return editing && !isLogTimeFormat(params.row.entityId as string) ? 'go-table__disabled' : '';
        },
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY.eventTime} />
        ),
        renderCell(params) {
          return (
            <ManualAnomalyDateCell
              validationService={service.validationService}
              params={params}
              timeZonesStore={timeZonesStore}
              format={(date) => {
                return date ? format(date, 'HH:mm:ss') : '-';
              }}
            />
          );
        },
        renderEditCell(params) {
          const isTimeFormat = isLogTimeFormat(params.row.entityId as string);
          return (
            <TableDatePicker
              cellParams={params}
              value={params.value}
              disabled={!isTimeFormat}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(
                  params.row.entityId as string,
                  'anomalyAt',
                  value ? getInverDate(value) : undefined,
                )
              }
              showTime
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'wellboreId',
        minWidth: 120,
        flex: 0.6,
        type: 'singleSelect',
        valueOptions: wellboreOptions,
        cellClassName: (params) => {
          const indexFormat = store.getFieldValue(params.row.entityId as string, 'indexFormat');
          return editing && indexFormat === 'LOG_TIME' ? 'go-table__disabled' : '';
        },
        valueFormatter(value) {
          const wellbore = wellboreOptions.find((o) => o.value === value);
          return wellbore ? wellbore.label : '-';
        },
        renderCell(params) {
          return <ManualAnomalyCell validationService={service.validationService} params={params} />;
        },
        renderEditCell(params) {
          const indexFormat = store.getFieldValue(params.row.entityId as string, 'indexFormat');
          return (
            <TableSelect
              showSearch
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={wellboreOptions}
              disabled={indexFormat === 'LOG_TIME'}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'wellboreId', value as number)
              }
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'riskLevel',
        width: 120,
        minWidth: 120,
        maxWidth: 120,
        resizable: false,
        type: 'singleSelect',
        valueOptions: riskLevelOptions,
        renderCell(params) {
          const risk = params.value as TRiskLevel;
          const label = riskLevelOptions.find((o) => o.value === risk)?.label ?? risk;
          return (
            <TableCell>
              {risk ? (
                <Tag status={riskToStatus[risk]} block style={{ width: '100%' }}>
                  {label}
                </Tag>
              ) : (
                '-'
              )}
            </TableCell>
          );
        },
        renderEditCell(params) {
          return (
            <TableSelect
              showSearch
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={riskLevelOptions}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId, 'riskLevel', value as TRiskLevel)
              }
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'author',
        type: 'string',
        editable: false,
        cellClassName: () => {
          return editing ? 'go-table__disabled' : '';
        },
        renderCell(params) {
          return (
            <UserInfo
              createdBy={params.row.createdBy}
              updatedBy={params.row.data.updatedBy}
              updatedAt={params.row.data.updatedAt}
              createdAt={params.row.createdAt}
              timeZonesStore={timeZonesStore}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'anomalyTypeId',
        minWidth: 120,
        flex: 0.6,
        type: 'singleSelect',
        valueOptions: anomalyTypeOptions,
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              showSearch
              cellParams={params}
              value={params.value}
              options={anomalyTypeOptions}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'anomalyTypeId', value as number)
              }
              allowClear
              onClear={() =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'anomalyTypeId', undefined)
              }
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'anomalySubtypeId',
        minWidth: 120,
        flex: 0.6,
        type: 'singleSelect',
        valueOptions: (params) =>
          params.row && params.row.data.anomalyTypeId ? getAnomalySubtypeOptions(params.row.data.anomalyTypeId) : [],
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={
                params.row && params.row.data.anomalyTypeId
                  ? getAnomalySubtypeOptions(params.row.data.anomalyTypeId)
                  : []
              }
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'anomalySubtypeId', value)
              }
              allowClear
              onClear={() =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'anomalySubtypeId', undefined)
              }
              showSearch
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'bitDepth',
        minWidth: 140,
        type: 'number',
        cellClassName: (params) => {
          return editing && isLogTimeFormat(params.row.entityId as string) ? 'go-table__disabled' : '';
        },
        renderHeader: (params) => (
          <TableHeader
            field={params.field}
            textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY[params.field] ?? params.field}
            icon={<DepthLightCustom />}
            unit={editing ? defaultDepthUnit : scaleDepthUnit}
          />
        ),
        renderCell(params) {
          const value = params.value as number | null;
          if (value == null) {
            return <TableCell>-</TableCell>;
          }
          let formattedValue = value;
          if (!editing) {
            formattedValue = Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
          }
          return <TableCell>{formattedValue}</TableCell>;
        },
        renderEditCell(params) {
          const isTimeFormat = isLogTimeFormat(params.row.entityId as string);
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value as number | null}
              allowClear
              disabled={isTimeFormat}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'bitDepth', value ?? null)
              }
            />
          );
        },
      },
      {
        ...textCell('holeDepth', { icon: <DepthLightCustom />, unit: editing ? defaultDepthUnit : scaleDepthUnit }),
        valueFormatter(_value) {
          if (_value == null) {
            return '-';
          }

          const value = Number(_value);

          if (!Number.isNaN(value)) {
            return Math.round(convertDepthUnitValue(value, defaultDepthUnit, scaleDepthUnit) * 100) / 100;
          }

          return '-';
        },
      },
      {
        ...baseColumn,
        field: 'unitId',
        type: 'singleSelect',
        valueOptions: unitOptions,
        renderEditCell(params) {
          return (
            <TableSelect
              autoFocus
              defaultOpen
              cellParams={params}
              value={params.value}
              options={unitOptions}
              showSearch
              optionFilterProp="label"
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'unitId', value as number)
              }
              allowClear
              onClear={() => store.onManualAnomalyItemValueChange(params.row.entityId as string, 'unitId', undefined)}
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'limitValue',
        type: 'string',
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              allowClear
              value={params.value as number | null}
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'limitValue', value ?? null)
              }
            />
          );
        },
      },
      {
        ...baseColumn,
        field: 'factValue',
        renderEditCell(params) {
          return (
            <TableNumberInput
              cellParams={params}
              value={params.value as number | null}
              allowClear
              onChange={(value) =>
                store.onManualAnomalyItemValueChange(params.row.entityId as string, 'factValue', value ?? null)
              }
            />
          );
        },
      },
      {
        ...textCell('deltaAbsolute'),
      },
      {
        ...textCell('deltaRelative'),
        minWidth: 100,
        flex: 0.5,
        renderCell(params) {
          const v = params.value as number | null;
          return <TableCell>{v == null ? '-' : `${v}%`}</TableCell>;
        },
      },
      {
        ...baseColumn,
        field: 'notification',
        minWidth: 140,
        flex: 1.2,
        renderEditCell(params) {
          return (
            <TableCell>
              <TableInput
                cellParams={params}
                style={{
                  width: '100%',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: 'inherit',
                }}
                value={String(params.value ?? '')}
                autoFocus
                onChange={(e) =>
                  store.onManualAnomalyItemValueChange(params.row.entityId as string, 'notification', e.target.value)
                }
              />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        field: 'recommendation',
        minWidth: 140,
        flex: 1,
        renderEditCell(params) {
          return (
            <TableCell>
              <TableInput
                cellParams={params}
                value={String(params.value ?? '')}
                autoFocus
                onChange={(e) =>
                  store.onManualAnomalyItemValueChange(params.row.entityId as string, 'recommendation', e.target.value)
                }
              />
            </TableCell>
          );
        },
      },
      {
        ...baseColumn,
        field: 'registrationDate',
        editable: false,
        sortable: true,
        filterable: true,
        resizable: true,
        minWidth: 110,
        flex: 0.5,
        valueGetter: (_, row) => store.getFieldValue(row.entityId as string, 'createdAt'),
        cellClassName: () => {
          return editing ? 'go-table__disabled' : '';
        },
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY.registrationDate} />
        ),
        renderCell(params) {
          const d = getCreatedAtZoned(params.row);
          return <TableCell>{d ? format(d, 'dd.MM.yyyy') : '-'}</TableCell>;
        },
      },
      {
        ...baseColumn,
        field: 'registrationTime',
        editable: false,
        minWidth: 100,
        valueGetter: (_, row) => store.getFieldValue(row.entityId as string, 'createdAt'),
        cellClassName: () => {
          return editing ? 'go-table__disabled' : '';
        },
        renderHeader: (params) => (
          <TableHeader field={params.field} textTKey={FIELD_TO_MANUAL_ANOMALY_COLUMN_TITLE_KEY.registrationTime} />
        ),
        renderCell(params) {
          const d = getCreatedAtZoned(params.row);
          return <TableCell>{d ? format(d, 'HH:mm:ss') : '-'}</TableCell>;
        },
      },
      {
        ...textCell('stageId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.workClassifierStageTypes,
      },
      {
        ...textCell('wellboreTypeId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.wellboreTypes,
      },
      {
        ...textCell('trajectoryTypeId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.trajectoryTypes,
      },
      {
        ...textCell('sectionTypeId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.sectionTypes,
      },
      {
        ...textCell('holeType', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: [
          { value: 'cased', label: t('alertsManagerWidget:autoAnomalies.holeType.cased') },
          { value: 'open', label: t('alertsManagerWidget:autoAnomalies.holeType.open') },
        ],
      },
      {
        ...textCell('stratUnitTypeId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.stratUnitTypes,
      },
      {
        ...textCell('reservoirId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.reservoirTypes,
      },
      {
        ...textCell('runTypeId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.runTypes,
      },
      {
        ...textCell('operationId', { readModeClassName: tableStyles.tableCellPurple }),
        type: 'singleSelect',
        valueOptions: service.operationTypes,
      },
    ];
  }, [
    editing,
    service.unitsList,
    service.workClassifierStageTypes,
    service.wellboreTypes,
    service.trajectoryTypes,
    service.sectionTypes,
    service.stratUnitTypes,
    service.reservoirTypes,
    service.runTypes,
    service.operationTypes,
    service.validationService,
    service.manualAnomaliesMap,
    indexFormatOptions,
    wellboreOptions,
    anomalyTypeOptions,
    store,
    timeZonesStore,
    getDate,
    riskLevelOptions,
    getAnomalySubtypeOptions,
    scaleDepthUnit,
    defaultDepthUnit,
    t,
  ]);
};
